import { useEffect, useMemo, useRef, useState } from 'react';
import { Event } from '../lib/types';
import { getEventImage, getEventMediaType, getEventVideoUrl, getEventGifUrl, getEventPosterUrl } from '../lib/utils';

interface EventMediaProps {
  event: Event;
  className?: string;
  showBlurBackground?: boolean;
  variant?: 'header' | 'cover';
  onImageClick?: () => void;
}

export default function EventMedia({ 
  event, 
  className = "w-full h-[60vh]", 
  showBlurBackground = true, 
  variant = 'header',
  onImageClick
}: EventMediaProps) {
  const mediaType = getEventMediaType(event);
  const imageUrl = getEventImage(event);
  const videoUrl = getEventVideoUrl(event);
  const gifUrl = getEventGifUrl(event);
  const posterUrl = getEventPosterUrl(event);

  // Environment capabilities and user preferences
  const [shouldReduceMotion, setShouldReduceMotion] = useState(false);
  const [saveDataMode, setSaveDataMode] = useState(false);
  const [effective2g, setEffective2g] = useState(false);
  const [userInitiatedPlay, setUserInitiatedPlay] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoFailed, setVideoFailed] = useState(false);

  const canPlayMp4 = useMemo(() => {
    if (typeof document === 'undefined') return true;
    const test = document.createElement('video');
    try {
      return typeof test.canPlayType === 'function' && !!test.canPlayType('video/mp4');
    } catch {
      return true; // be permissive on SSR/unknown
    }
  }, []);

  useEffect(() => {
    // Reduced motion
    const mq = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    const updateMotion = () => setShouldReduceMotion(!!mq?.matches);
    updateMotion();
    mq?.addEventListener?.('change', updateMotion);
    return () => mq?.removeEventListener?.('change', updateMotion);
  }, []);

  useEffect(() => {
    // Network information API
    const navConn: any = (navigator as any)?.connection;
    const updateConn = () => {
      setSaveDataMode(!!navConn?.saveData);
      const type = String(navConn?.effectiveType || '').toLowerCase();
      setEffective2g(type === '2g' || type === 'slow-2g');
    };
    updateConn();
    navConn?.addEventListener?.('change', updateConn);
    return () => navConn?.removeEventListener?.('change', updateConn);
  }, []);

  // Determine whether to load/play video automatically
  const shouldAutoLoadVideo = useMemo(() => {
    if (mediaType !== 'video') return false;
    if (!canPlayMp4) return false;
    if (shouldReduceMotion) return false;
    if (saveDataMode || effective2g) return false;
    return true;
  }, [mediaType, canPlayMp4, shouldReduceMotion, saveDataMode, effective2g]);

  // Pause/resume when offscreen
  useEffect(() => {
    if (!videoRef.current) return;
    const el = videoRef.current;
    if (!shouldAutoLoadVideo && !userInitiatedPlay) return; // not loading video yet

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!el) return;
          if (entry.isIntersecting) {
            // try play when visible
            el.play().catch(() => {/* ignore */});
          } else {
            el.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shouldAutoLoadVideo, userInitiatedPlay]);

  // Nudge autoplay on mount or after user initiation (some browsers require an explicit play call)
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (mediaType !== 'video') return;
    if (shouldAutoLoadVideo || userInitiatedPlay) {
      el.play().catch(() => {/* ignore */});
    }
  }, [mediaType, shouldAutoLoadVideo, userInitiatedPlay]);
  
  // Background blur always uses an image-like poster (never video/gif URL)
  const backgroundUrl = posterUrl || imageUrl;
  
  // Cover variant - simpler background cover style
  if (variant === 'cover') {
    return (
      <div 
        className={`${className} bg-cover bg-center ${onImageClick ? 'cursor-pointer' : ''}`}
        style={{ backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined }}
        onClick={onImageClick}
      />
    );
  }
  
  // Header variant - complex blur background + object-contain foreground
  return (
    <div className={`${className} relative overflow-hidden ${onImageClick ? 'cursor-pointer' : ''}`}>
      {/* Blurred background layer */}
      {showBlurBackground && (
        <div 
          className="absolute inset-0 bg-cover bg-center filter blur-lg scale-110"
          style={{ backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined }}
        />
      )}
      
      {/* Media content layer */}
      {mediaType === 'video' && !videoFailed ? (
        shouldAutoLoadVideo || userInitiatedPlay ? (
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-contain"
            src={videoUrl}
            poster={posterUrl || undefined}
            preload="metadata"
            autoPlay
            muted
            loop
            playsInline
            onClick={onImageClick}
            onLoadedMetadata={() => {
              const el = videoRef.current; 
              el?.play?.().catch(() => {/* ignore */});
            }}
            onError={() => setVideoFailed(true)}
          />
        ) : (
          <button
            type="button"
            className="absolute inset-0 w-full h-full flex items-center justify-center"
            onClick={() => {
              setUserInitiatedPlay(true);
              // try to play on next tick after video mounts
              setTimeout(() => videoRef.current?.play?.().catch(() => {/* ignore */}), 0);
            }}
            aria-label="Play video"
          >
            {/* Poster image with play overlay */}
            <div className="absolute inset-0 bg-contain bg-center bg-no-repeat" style={{ backgroundImage: posterUrl ? `url(${posterUrl})` : undefined }} />
            <div className="relative z-10 rounded-full bg-black/50 p-4 text-white">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </button>
        )
      ) : mediaType === 'gif' ? (
        <img
          className="absolute inset-0 w-full h-full object-contain"
          src={gifUrl}
          alt="Event"
          onClick={onImageClick}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div 
          className="absolute inset-0 bg-contain bg-center bg-no-repeat"
          style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : undefined }}
          onClick={onImageClick}
        />
      )}
    </div>
  );
}
